/**
 * Phase 2 Wave 2 (Task 1) — classify an `(prev, next)` entity pair into
 * a coarse "what changed?" bucket the renderer can act on without
 * inspecting every field itself.
 *
 * # Buckets
 *
 *   - `"style"`    — only style fields (layer / color / linetype /
 *                    lineweight / transparency) changed. Tessellation
 *                    cache is still valid; the renderer can re-bucket
 *                    in O(1) without re-running geometry.
 *   - `"geometry"` — only geometry fields changed. Style bucket key
 *                    stays the same; tessellation cache MUST be busted.
 *   - `"both"`     — both kinds changed. Re-bucket AND re-tessellate.
 *   - `"other"`    — neither kind changed (annotative-only / array
 *                    membership / arbitrary metadata). Renderer can
 *                    skip both paths.
 *
 * # Why a coarse classification?
 *
 * The renderer's incremental-update path (Task 2: `patchEntity` /
 * `updateEntityBucket`) only needs to know whether the bucket key OR
 * the vertex stream needs to change. Per-field granularity would
 * require keeping per-field versions on every entity — a 10× metadata
 * cost for no extra benefit at this layer. The four-way bucket matches
 * what the renderer can actually do differently.
 *
 * # Why this helper is decoupled from `mutationStore`?
 *
 * Pure data — testable without standing up the compact cache or the
 * metadata index. Imported by both `mutationStore.setInCompactCache`
 * (write-through path) and by the test suite directly.
 *
 * # AutoCAD parity
 *
 * Internal optimization — does not alter behavior, only routing.
 */

import type { DrawingEntity } from "@dxf-ai-editor/drawing-model";

export type EntityChangedFields = "geometry" | "style" | "both" | "other";

/**
 * The set of style fields shared across every `DrawingEntity` type.
 * Lifted from `drawingEntityBaseSchema` in `packages/drawing-model/src/model.ts`.
 * Changes to these affect bucket assignment (`layer × color × linetype`)
 * and per-instance state but do NOT invalidate tessellation.
 */
const STYLE_FIELDS = [
  "layer",
  "color",
  "linetype",
  "lineweight",
  "transparency",
] as const;

/**
 * Per-entity-type geometry fields — those whose change moves vertices
 * in the resulting line/mesh stream. Anything not listed and not in
 * `STYLE_FIELDS` is treated as `"other"` (annotative context,
 * arrayGroupId, etc.).
 *
 * Each list is a flat enumeration of top-level property names on the
 * entity. Nested deep-equality is handled by `shallowEqualValue`.
 */
// F5 (codex P2) — field names mirror the actual Zod schemas in
// `packages/drawing-model/src/model.ts`. Earlier drafts of this map
// used "startParameter"/"endParameter"/"content"/"text"/etc. — those
// names do not exist on any DrawingEntity and the classifier silently
// reported `"other"` for genuine geometry changes (e.g. an ellipse
// `endParam` edit, a TEXT `contents` edit, a Dimension `dimensionText`
// edit) — causing the renderer to skip tessellation invalidation and
// leave stale geometry on screen. Every name below was cross-checked
// against the discriminated-union member schema.
const GEOMETRY_FIELDS_BY_TYPE: Record<string, ReadonlyArray<string>> = {
  Line: ["start", "end", "thickness"],
  Point: ["location", "rotation", "thickness"],
  Circle: ["center", "radius", "thickness"],
  Arc: ["center", "radius", "startAngle", "endAngle", "thickness"],
  Ellipse: [
    "center",
    "majorAxisEndpoint",
    "ratio",
    "startParam",
    "endParam",
    "extrusion",
    "thickness",
  ],
  Spline: [
    "degree",
    "controlPoints",
    "knots",
    "weights",
    "fitPoints",
    "closed",
    "periodic",
    "rational",
    "planar",
    "startTangent",
    "endTangent",
  ],
  LWPOLYLINE: [
    "vertices",
    "closed",
    "constantWidth",
    "elevation",
    "thickness",
  ],
  Text: [
    "insertionPoint",
    "alignmentPoint",
    "height",
    "rotation",
    "widthFactor",
    "obliqueAngle",
    "hJustification",
    "vJustification",
    "contents",
    "styleName",
    "generationFlags",
    "extrusion",
    "thickness",
  ],
  MText: [
    "insertionPoint",
    "nominalHeight",
    "rotation",
    "referenceWidth",
    "attachmentPoint",
    "drawingDirection",
    "lineSpacingFactor",
    "lineSpacingStyle",
    "contents",
    "styleName",
    "xAxisDirection",
    // Codex #342 round-8 Finding C — a default MTEXT mirrored across a HORIZONTAL
    // axis changes ONLY `extrusion` ((0,0,1)→(0,0,−1)); rotation + xAxisDirection
    // stay put. Without `extrusion` here the change classified as "other" and the
    // incremental render no-op'd, leaving the mirror visually unflipped until a
    // full rebuild. The live mesh derives its glyph-frame flip from the extrusion
    // (isReflectedOcs), so an extrusion-only change IS a geometry change. (Text
    // already lists `extrusion`; MText was the lone gap.)
    "extrusion",
    "columnType",
    "columnCount",
    "columnWidth",
    "columnGutter",
    "columnHeights",
    "horizontalWidth",
    "verticalHeight",
  ],
  Dimension: [
    "defPoint",
    "textMidpoint",
    "extLine1Point",
    "extLine2Point",
    "thirdPoint",
    "arcPoint",
    "textRotation",
    "linearAngle",
    "obliqueAngle",
    "textAngleRelativeToDimLine",
    // GitHub Codex review PR #558 round 2 P2 — handedness companion to
    // `textAngleRelativeToDimLine`; changes the rendered glyph flip
    // (`dimensionGeometry.ts`'s `mirrored` primitive field), same class as
    // Multileader's own `textMirrored` entry below. A Dimension lying
    // entirely on the mirror axis with a zero relative angle can have
    // MIRRTEXT=1 change ONLY this field — without it here,
    // `classifyEntityChange` returns "other", which tells the incremental
    // renderer to SKIP re-tessellation, leaving the old unmirrored mesh
    // visible.
    "textMirrored",
    "leaderLength",
    "measurement",
    "dimensionText",
    "styleName",
    "dimensionType",
    "textHeight",
    "attachmentPoint",
    "styleOverrides",
  ],
  Multileader: [
    "textLocation",
    "basePoint",
    "leaderRoots",
    "textContents",
    "textStyleName",
    "textHeight",
    "textRotation",
    // GitHub Codex review PR #558 (post-rebase fresh pass) P2 — handedness
    // companion to `textRotation`; changes the rendered glyph flip
    // (`sceneMultileaderMeshFactories.ts`'s `mirrored` primitive field).
    "textMirrored",
    // GitHub Codex review PR #558 round 2 (round-9 GitHub bot follow-up)
    // P2 — the mirror reducer rewrites EVERY `annotativeContexts[].
    // textRotation` (`geometryReducerEdit.ts`'s Multileader mirror
    // branch), and `multileaderFromEntity.ts` explicitly PREFERS an
    // active annotative context's OWN `textRotation` over the flat field
    // above (`annoCtx?.textRotation ?? entity.textRotation`) — same class
    // as Dimension's own `textMirrored` entry above this list. An MLeader
    // whose flat `textRotation` stays 0 (e.g. all leader points on a
    // vertical mirror axis under MIRRTEXT=0) but whose ACTIVE context's
    // OWN rotation changes would otherwise classify as "other", telling
    // the incremental renderer to skip re-tessellation and leave the
    // stale pre-mirror text mesh on screen. `secondaryContexts` is
    // deliberately NOT added here — those blocks are round-trip/export
    // preservation for scales OTHER than the currently active one
    // (`dxfBridge.mleaderContextConversion.ts`'s "re-emit verbatim" doc),
    // never consulted by the LIVE render's CURRENT-scale selection.
    "annotativeContexts",
    "textWidth",
    "textAttachmentType",
    "textAttachmentDirection",
    "textLineSpacingStyle",
    "textLineSpacingFactor",
    "textLineSpacingDistance",
    "styleName",
    "contentType",
    "arrowheadSize",
    "arrowheadBlock",
    "leaderLineType",
    "landingEnabled",
    "doglegEnabled",
    "doglegLength",
    "landingGap",
    "breakSize",
    "blockName",
    "blockScale",
    "blockRotation",
    "blockConnectionType",
  ],
  Hatch: [
    "boundaryPaths",
    "patternName",
    "patternScale",
    "patternAngle",
    "solidFill",
    "hatchStyle",
    "patternType",
    "doubleLine",
    "elevation",
    "seedPoints",
  ],
  Block: [
    "blockName",
    "insertionPoint",
    "rotation",
    "scaleX",
    "scaleY",
    "scaleZ",
    "columnCount",
    "rowCount",
    "columnSpacing",
    "rowSpacing",
    "extrusion",
    "attributes",
    "visibilityState",
    "customProperties",
    "representationOverrides",
  ],
  "Rectangular Associative Array": [
    "basePoint",
    "rowCount",
    "columnCount",
    "rowSpacing",
    "columnSpacing",
    "rowTotal",
    "columnTotal",
    "axisAngle",
    "associative",
    "sourceEntityIds",
  ],
  "Revision Cloud": [
    "vertices",
    "arcChordLength",
    "approximateArcLength",
    "revisionCloudStyle",
    "creationMode",
    "closed",
  ],
  Wipeout: ["vertices", "frame"],
  Viewport: [
    "center",
    "width",
    "height",
    "viewCenter",
    "viewHeight",
    "viewDirection",
    "viewTarget",
    "viewTwistAngle",
    "lensLength",
    "frontClipZ",
    "backClipZ",
    "viewportStatus",
    "displayLocked",
    "frozenLayers",
    "viewportCannoscaleName",
    "viewportCannoscaleFactor",
    "viewportAnnoAllVisible",
  ],
};

/**
 * Deep-equal-ish check for entity field values. Handles primitive
 * equality, NaN equality, and one level of nested object / array
 * comparison (covers all the (x, y, z) point shapes + simple array
 * fields like `vertices` or `boundaryPaths`).
 *
 * Falls back to JSON.stringify for any structure deeper than two
 * levels — slow but correct, and rarely hit because most geometry
 * payloads are flat arrays of points.
 */
function shallowEqualValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a === "number" && typeof b === "number") {
    return Number.isNaN(a) && Number.isNaN(b);
  }
  if (a === null || b === null) return false;
  if (typeof a !== "object" || typeof b !== "object") return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!shallowEqualValue(a[i], b[i])) return false;
    }
    return true;
  }
  // Object pair — compare own enumerable keys.
  const aRec = a as Record<string, unknown>;
  const bRec = b as Record<string, unknown>;
  const ak = Object.keys(aRec);
  const bk = Object.keys(bRec);
  if (ak.length !== bk.length) return false;
  for (const key of ak) {
    if (!Object.prototype.hasOwnProperty.call(bRec, key)) return false;
    const av = aRec[key];
    const bv = bRec[key];
    if (av === bv) continue;
    // Fast path for nested points / primitives.
    if (typeof av === "object" && av !== null) {
      if (typeof bv !== "object" || bv === null) return false;
      if (!shallowEqualValue(av, bv)) return false;
    } else if (av !== bv) {
      // Differs and neither is a nested object.
      if (typeof av === "number" && typeof bv === "number") {
        if (Number.isNaN(av) && Number.isNaN(bv)) continue;
      }
      return false;
    }
  }
  return true;
}

/**
 * Compare prev / next entities and return the change-class bucket.
 *
 * Implementation walks STYLE_FIELDS first (5 fields, fixed cost), then
 * the type-specific GEOMETRY_FIELDS list. Any remaining field
 * difference (annotative scale, arrayGroupId, ...) is reported as
 * `"other"` and does not bump either flag.
 */
export function classifyEntityChange(
  prev: DrawingEntity,
  next: DrawingEntity,
): EntityChangedFields {
  let styleChanged = false;
  let geometryChanged = false;

  const prevRec = prev as unknown as Record<string, unknown>;
  const nextRec = next as unknown as Record<string, unknown>;

  for (const field of STYLE_FIELDS) {
    if (!shallowEqualValue(prevRec[field], nextRec[field])) {
      styleChanged = true;
      break;
    }
  }

  // If the type itself changed, treat the whole entity as a geometry
  // change (we can't reuse the tessellation between two different
  // primitive shapes).
  if (prev.type !== next.type) {
    geometryChanged = true;
  } else {
    const fields = GEOMETRY_FIELDS_BY_TYPE[prev.type] ?? [];
    for (const field of fields) {
      if (!shallowEqualValue(prevRec[field], nextRec[field])) {
        geometryChanged = true;
        break;
      }
    }
  }

  if (styleChanged && geometryChanged) return "both";
  if (geometryChanged) return "geometry";
  if (styleChanged) return "style";
  return "other";
}

/**
 * The canonical list of style field names, exported for callers that
 * need to inspect them (renderer's bucket-key reconciliation).
 */
export const STYLE_FIELD_NAMES: ReadonlyArray<string> = STYLE_FIELDS;

/**
 * The canonical geometry-field lookup, exported for callers that need
 * to query per-type field lists (telemetry, future granular-version
 * indexing).
 */
export const GEOMETRY_FIELD_NAMES: Record<string, ReadonlyArray<string>> =
  GEOMETRY_FIELDS_BY_TYPE;
